// Demo-seed for the assignment-grid "Show Work" metrics.
//
// When the URL has `?demo=seed`, the at-a-glance teacher dashboard displays
// hardcoded plausible typing-minutes + paste-words per student instead of the
// real (empty) values. Used only for Loom recordings; the persisted data is
// not changed.
//
// To disable, just visit the page without `?demo=seed`.

const HEAVY_PASTE_WORD_THRESHOLD = 10;

// Designated pasters for the demo. Both names appear in Danny's demo classroom.
// Annabeth is a heavy paster (flagged for review), Greg is a light paster
// (visible but unflagged) — gives the rollup banner the cleanest demo line:
// "2 students copy-pasted (1 flagged for review)".
const PASTERS: Record<string, { writeTimeMs: number; pastedWords: number }> = {
  'Annabeth Chase': { writeTimeMs: 9 * 60 * 1000 + 12 * 1000, pastedWords: 38 },
  'Greg Heffley':   { writeTimeMs: 2 * 60 * 1000 + 45 * 1000, pastedWords: 8 },
};

export function isDemoSeed(search: string | URLSearchParams): boolean {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  return params.get('demo') === 'seed';
}

// Deterministic hash so the same student always gets the same minutes.
function nameHash(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i += 1) {
    h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function getSeededShowWork(
  name: string,
  isStarted: boolean,
): { writeTimeMs: number; pastedWords: number } {
  if (!isStarted) return { writeTimeMs: 0, pastedWords: 0 };
  const trimmed = (name || '').trim();
  if (PASTERS[trimmed]) return PASTERS[trimmed];

  // Plausible write time: 3-13 minutes plus a sub-minute offset, deterministic
  // per name so values don't shift between refreshes.
  const h = nameHash(trimmed);
  const minutes = 3 + (h % 11);
  const seconds = (h >> 4) % 60;
  return {
    writeTimeMs: (minutes * 60 + seconds) * 1000,
    pastedWords: 0,
  };
}

// Pre-aggregate the rollup counts using the seeded data so the banner at the
// top of the assignment grid matches the per-card numbers.
export function getSeededRollupShowWork(
  entries: Array<{
    user?: { name?: string; surname?: string };
    submission?: { status?: string } | null;
  }>,
): { pastersCount: number; heavyPastersCount: number } {
  let pastersCount = 0;
  let heavyPastersCount = 0;
  entries.forEach((e) => {
    const status = e.submission?.status;
    const isStarted = !!status && status !== 'new';
    if (!isStarted) return;
    const name = `${e.user?.name || ''} ${e.user?.surname || ''}`.trim();
    const seed = getSeededShowWork(name, true);
    if (seed.pastedWords > 0) pastersCount += 1;
    if (seed.pastedWords > HEAVY_PASTE_WORD_THRESHOLD) heavyPastersCount += 1;
  });
  return { pastersCount, heavyPastersCount };
}
